import { PROCESS_SYSTEM_SOURCE } from './processSystemSource';

export type ProcessTranslator = (
  key: string,
  options?: Record<string, unknown>,
) => string;

interface RoleDisplayInput {
  category?: string | null;
  name: string;
  description?: string | null;
}

interface NodeDisplayInput {
  name: string;
  type?: string | null;
  description?: string | null;
  configuration?: Record<string, unknown> | null;
}

interface DisplayText {
  name: string;
  description: string;
}

interface FormPropertyInput {
  name: string;
  label: string;
  type: string;
  description?: string;
  defaultValue?: unknown;
  options?: Array<{ value: string; label: string }>;
  required?: boolean;
}

interface FormComponentDisplayInput {
  isSystem?: boolean;
  name: string;
  label: string;
  category: string;
  description?: string | null;
  properties?: FormPropertyInput[];
}

interface FormTemplateDisplayInput {
  isSystem?: boolean;
  name: string;
  category: string;
  difficulty: string;
  description?: string | null;
}

export const LOCAL_ACCEPTANCE_SYSTEM_KEYS = [
  'local_acceptance_leave_request',
  'local_acceptance_purchase_approval',
  'local_acceptance_it_access_request',
  'local_acceptance_vendor_contract_review',
] as const;

export type LocalAcceptanceSystemKey = (typeof LOCAL_ACCEPTANCE_SYSTEM_KEYS)[number];

export type SystemDefinitionKey =
  | 'general_approval'
  | 'purchase_request'
  | 'it_access_request'
  | AdoptedSystemKey
  | LocalAcceptanceSystemKey;

/** The eight definitions `defaults.py` ADOPTED on 2026-08-24 from orphan `(Template)` rows.
 *
 * They had lived as `(Template)`-suffixed definitions with NO `system_key`. The adoption gave each
 * one a `system_key` and dropped the suffix — and that rename is the whole defect the owner
 * reported on 2026-09-02 as *"很多 process 模版没有国际化"*. Their translations DID exist, under the
 * name-keyed `liveDisplay.systemProcessTemplates` table, whose match key was the old suffixed name;
 * the rename orphaned all ten entries at once and nobody moved them here.
 *
 * `seededDefinitionParity.test.ts` now derives this list from `PROCESS_DEFINITION_DEFAULTS`, so a
 * twelfth seed cannot ship without a label.
 */
export const ADOPTED_SYSTEM_KEYS = [
  'leave_request',
  'overtime_request',
  'expense_report',
  'business_trip',
  'procurement_request',
  'contract_approval',
  'onboarding',
  'offboarding',
] as const;

export type AdoptedSystemKey = (typeof ADOPTED_SYSTEM_KEYS)[number];

interface ProcessDefinitionDisplayInput {
  system_key?: string | null;
  systemKey?: string | null;
  form_schema?: { system_key?: string | null; origin_system_key?: string | null } | null;
  formSchema?: { system_key?: string | null; origin_system_key?: string | null } | null;
  name: string;
  description?: string | null;
}

interface DefinitionFieldDisplayInput {
  id?: string | null;
  label?: string | null;
  properties?: {
    label?: string | null;
    options?: Array<string | { label: string; value: string }>;
    /** The BODY of a `static_text` field. Not a label — it is the paragraph the requester reads,
     *  and it was the last English string left inside two otherwise-localized seeded templates. */
    text?: string | null;
  } | null;
}

interface DefinitionNodeDisplayInput {
  id?: string | null;
  label?: string | null;
  data?: { label?: string | null } | null;
}

interface PolicyDisplayInput {
  isSystem?: boolean;
  kind: string;
  name: string;
  description?: string | null;
}

export interface FormComponentDisplay extends DisplayText {
  label: string;
  properties: FormPropertyInput[];
}

const PROCESS_STATUS_KEYS: Record<string, string> = {
  all: 'all',
  draft: 'draft',
  active: 'active',
  deprecated: 'deprecated',
  archived: 'archived',
};

const ROLE_CATEGORY_KEYS: Record<string, string> = {
  system: 'categorySystem',
  custom: 'categoryCustom',
  department: 'categoryDepartment',
};





/** Statuses `translateKnownCode` will look up. An ALLOWLIST, so an unknown backend value falls
 *  through to itself rather than rendering a missing-key string.
 *
 *  `withdrawn` was MISSING while all nine locales already carried its copy ("已撤回" / "Withdrawn"
 *  / …), so the gate refused the lookup and printed the raw value — the owner saw an untranslated
 *  "Withdrawn" chip on a zh-CN instance list, 2026-09-01. The translation existed; its own
 *  doorman kept it out.
 *
 *  That is the defect class this list creates, so `liveStatusCoverage.test.ts` now derives the
 *  requirement instead of trusting the list: every status any locale has copy for must be
 *  admitted here. Adding copy without adding the key is the exact mistake, and it is now red. */
const LIVE_STATUS_KEYS: Record<string, string> = Object.fromEntries([
  'active', 'inactive', 'disabled', 'expired', 'revoked', 'draft', 'submitted',
  'in_review', 'approved', 'rejected', 'delegated', 'completed', 'cancelled',
  'withdrawn',
  'pending', 'in_progress', 'review', 'running', 'waiting', 'suspended', 'failed',
  'deprecated', 'archived',
].map((value) => [value, value]));

const PRIORITY_KEYS: Record<string, string> = Object.fromEntries(
  ['urgent', 'critical', 'high', 'medium', 'low', 'normal'].map((value) => [value, value]),
);
const OUTCOME_KEYS: Record<string, string> = Object.fromEntries(
  ['approved', 'rejected', 'completed', 'cancelled'].map((value) => [value, value]),
);
const SLA_KEYS: Record<string, string> = Object.fromEntries(
  ['on_track', 'at_risk', 'breached'].map((value) => [value, value]),
);
const OVERRIDE_TYPE_KEYS: Record<string, string> = Object.fromEntries(
  ['approval', 'routing', 'deadline', 'condition'].map((value) => [value, value]),
);
/**
 * A COMPONENT's category — the kind of input it is. Split from the template vocabulary on
 * 2026-08-10; see `FormComponents/helpers.ts` for why one shared map produced "Financial"
 * (a currency field) next to "Finance" (a department), and two identical "HR"s.
 */
const FIELD_FAMILY_KEYS: Record<string, string> = Object.fromEntries(
  ['basic', 'advanced', 'selection', 'media', 'financial', 'hr', 'special', 'business']
    .map((value) => [value, value]),
);
/** A TEMPLATE's category — which part of the business uses the form. */
const TEMPLATE_DOMAIN_KEYS: Record<string, string> = Object.fromEntries(
  ['hr', 'finance', 'it', 'operations', 'general', 'business', 'other']
    .map((value) => [value, value]),
);
const FORM_DIFFICULTY_KEYS: Record<string, string> = Object.fromEntries(
  ['easy', 'medium', 'hard'].map((value) => [value, value]),
);
const FORM_PROPERTY_TYPE_KEYS: Record<string, string> = Object.fromEntries(
  ['string', 'number', 'boolean', 'textarea'].map((value) => [value, value]),
);

export const DELEGATION_PROCESS_OPTIONS = [
  { value: 'Leave Approval', i18nKey: 'leaveApproval' },
  { value: 'Expense Approval', i18nKey: 'expenseApproval' },
  { value: 'Purchase Order', i18nKey: 'purchaseOrder' },
  { value: 'Time Off Request', i18nKey: 'timeOffRequest' },
  { value: 'Budget Approval', i18nKey: 'budgetApproval' },
] as const;

const POLICY_OPTION_VALUES = [
  'equals', 'not_equals', 'in', 'not_in', 'greater_than', 'greater_than_or_equal',
  'less_than', 'less_than_or_equal', 'between', 'contains', 'starts_with', 'ends_with',
  'exists', 'event', 'schedule', 'condition', 'data_change', 'api_call', 'time_based',
  'file_upload', 'status_change', 'threshold', 'external_event', 'approve', 'reject',
  'notify', 'update', 'create', 'delete', 'transform', 'evaluate', 'assign', 'escalate',
  'extract', 'classify', 'route', 'validate', 'send', 'test', 'branch', 'scan', 'flag',
  'alert', 'log', 'calculate', 'analyze', 'respond', 'timeout', 'validation', 'security',
  'network', 'data', 'business_rule', 'system', 'integration', 'retry', 'pause', 'cancel',
  'fallback', 'rollback', 'compensate', 'and', 'or',
] as const;

export const POLICY_OPTIONS = Object.fromEntries(
  POLICY_OPTION_VALUES.map((value) => [value, {
    value,
    i18nKey: `processes:liveDisplay.policyOptions.${value}`,
  }]),
) as Record<string, { value: string; i18nKey: string }>;

/** Seeded process roles, matched by their ENGLISH NAME because `process_roles` has no stable key.
 *
 * THE DEFECT (owner, 2026-09-01): `Process Owner` and `Finance Approver` rendered in English on a
 * zh-CN Roles page while the six above them were localized. The backend seeds exactly those two
 * (`app/processes/defaults.PROCESS_ROLE_DEFAULTS`, added 2026-08-22) and nobody extended this list,
 * so `getProcessRoleDisplay` found no canonical entry and fell through to the raw column.
 *
 * THE ROOT CAUSE IS THE MATCH KEY, not the missing rows. Every sibling surface keys off something
 * stable — process definitions use `system_key`, form components and nodes use a name the backend
 * treats as an identifier — while a process ROLE has only `name` (String(100)) and
 * `category='system'`. Matching display copy to a human-readable name means the copy breaks the
 * day someone renames a seed, and it gives a new seed no way to announce itself.
 *
 * Adding a `system_key` to `process_roles` is the real fix and it is a migration plus a seed
 * change plus an operator surface; it is written up in HANDOFF_2026-09-01 rather than smuggled in
 * here. What ships now is the two missing rows and `seededProcessRoleNames.test.ts`, which reads
 * the backend's own seed file and fails when the two lists disagree — so the next seeded role
 * cannot arrive un-localized even while the fragile match key remains.
 */
const SYSTEM_ROLE_DEFAULTS = [
  ['initiator', 'Initiator', 'Process initiator. Can submit requests and track own instances.'],
  ['directManager', 'Direct Manager', 'Approves requests from direct reports.'],
  ['departmentHead', 'Department Head', 'Department-level approver with escalation authority.'],
  ['hrManager', 'HR Manager', 'HR process approvals for leave, onboarding, and offboarding.'],
  ['financeManager', 'Finance Manager', 'Finance-gated approvals for expenses, procurement, and contracts.'],
  ['admin', 'Admin', 'Full access to process definitions, roles, nodes, and policies.'],
  // Seeded by `PROCESS_ROLE_DEFAULTS` since 2026-08-22. The English here must stay byte-identical
  // to that file's `name` — it IS the match key.
  ['processOwner', 'Process Owner', 'Fallback approver for general and operational requests.'],
  ['financeApprover', 'Finance Approver', 'Approves purchase requests above the starter-pack threshold.'],
] as const;

const SYSTEM_NODE_DEFAULTS = [
  ['startEvent', 'Start Event', 'start_event', 'Process entry point. Every flow starts here.'],
  ['endEvent', 'End Event', 'end_event', 'Process termination. Marks the instance as completed.'],
  ['managerApproval', 'Manager Approval', 'user_task', "Approval by the requester's line manager."],
  ['financeApproval', 'Finance Approval', 'user_task', 'Finance team review for organization spending.'],
  ['legalReview', 'Legal Review', 'user_task', 'Legal team review for contracts and policy changes.'],
  ['emailNotification', 'Email Notification', 'mail_task', 'Send a templated email to a role, recipient list, or requester.'],
  ['exclusiveGateway', 'Exclusive Gateway', 'exclusive_gateway', 'Branch on a condition so exactly one outgoing edge is taken.'],
  ['parallelGateway', 'Parallel Gateway', 'parallel_gateway', 'Fork into parallel branches and merge downstream.'],
  ['timerWait', 'Timer Wait', 'timer_intermediate_event', 'Pause the flow for a configured duration before continuing.'],
  ['serviceTask', 'Service Task', 'service_task', 'Machine-executed step such as an API call, script, or webhook.'],
] as const;

/**
 * The engine's timeline actions, MEASURED from the producer rather than retyped.
 *
 * This list was hand-typed and had drifted in both directions (2026-08-26 audit): it was
 * missing `node_entered` — the most frequent event of all, so the timeline showed a raw
 * code on nearly every instance — and `task_reassigned`, while carrying four keys
 * (`approved`, `failed`, `nudged`, `rejected`) that nothing emits.
 *
 * Kept as a literal rather than derived from the locale file, because the CHECK belongs in
 * a test that can read the backend: `processCodeTables.test.ts` extracts every
 * `_add_timeline(...)` action from `app/processes/engine.py` + `service.py` and requires
 * this array to equal it. So the array is still typed, but it can no longer be WRONG
 * without going red — which is the property that was missing, not the typing.
 */
const TIMELINE_ACTION_KEYS: Record<string, string> = Object.fromEntries(
  [
    'branched', 'cancelled', 'node_entered', 'rerouted', 'retried', 'started',
    'task_approved', 'task_forwarded', 'task_reassigned', 'task_rejected',
    'task_spawned', 'withdrawn',
  ].map((value) => [value, value]),
);

const SYSTEM_FORM_COMPONENT_DEFAULTS = [
  ['textField', 'text_field'],
  ['textareaField', 'textarea_field'],
  ['numberField', 'number_field'],
  ['currencyField', 'currency_field'],
  ['datePicker', 'date_picker'],
  ['selectDropdown', 'select_dropdown'],
  ['organizationLocation', 'organization_location'],
  ['fileAttachment', 'file_attachment'],
  ['invoiceFile', 'invoice_file'],
  // 2026-08-24. Without these three the Designer rendered the RAW runtime type —
  // `worker_reference`, `department_reference`, `static_text` — beside every field, while the
  // nine older types showed a translated label. The types shipped 08-24 and this map was the
  // surface nobody updated.
  ['workerReference', 'worker_reference'],
  ['departmentReference', 'department_reference'],
  ['staticText', 'static_text'],
  ['placeField', 'place_field'],
  // Added 2026-08-27 with the two new field types. Their absence made the canvas render
  // the RAW CODE `duration_field` in the type column while every sibling row showed a
  // translated name — `translateFormComponentCode` falls through to `original` on a miss,
  // silently. The gate now DERIVES this set from the backend so a 16th type cannot ship
  // without a label.
  ['multiSelect', 'multi_select'],
  ['durationField', 'duration_field'],
] as const;

/** Seeded form templates, matched by their ENGLISH NAME because `form_templates` has no
 * `system_key` column — the same fragile match key as `process_roles` (see SYSTEM_ROLE_DEFAULTS).
 *
 * Eight of the eleven were missing until 2026-09-02. Three arrived with
 * `defaults.FORM_TEMPLATE_DEFAULTS`; the other eight were seeded by migrations 099 and 103, which
 * no frontend list ever tracked. `seededDefinitionParity.test.ts` derives the expected set from
 * both producers so the next one cannot ship un-localized.
 */
const SYSTEM_FORM_TEMPLATE_DEFAULTS = [
  ['generalApproval', 'General Approval'],
  ['purchaseRequest', 'Purchase Request'],
  ['itAccessRequest', 'IT Access Request'],
  ['leaveRequest', 'Leave Request'],
  ['overtimeRequest', 'Overtime Request'],
  ['expenseReport', 'Expense Report'],
  ['businessTrip', 'Business Trip'],
  ['procurementRequest', 'Procurement Request'],
  ['contractApproval', 'Contract Approval'],
  ['onboardingChecklist', 'Onboarding Checklist'],
  ['offboardingChecklist', 'Offboarding Checklist'],
] as const;

const SYSTEM_DEFINITION_KEYS: Record<SystemDefinitionKey, true> = {
  general_approval: true,
  purchase_request: true,
  it_access_request: true,
  leave_request: true,
  overtime_request: true,
  expense_report: true,
  business_trip: true,
  procurement_request: true,
  contract_approval: true,
  onboarding: true,
  offboarding: true,
  local_acceptance_leave_request: true,
  local_acceptance_purchase_approval: true,
  local_acceptance_it_access_request: true,
  local_acceptance_vendor_contract_review: true,
};

export const SYSTEM_DEFINITION_FIELDS: Record<SystemDefinitionKey, readonly string[]> = {
  general_approval: ['request_title', 'request_details', 'needed_by'],
  purchase_request: ['item_or_service', 'quantity', 'estimated_cost', 'needed_by', 'business_need'],
  it_access_request: ['system_or_resource', 'access_level', 'justification'],
  leave_request: ['leave_note', 'leave_type', 'start_date', 'end_date', 'total_days', 'reason'],
  overtime_request: ['overtime_date', 'hours', 'reason'],
  expense_report: ['expense_date', 'category', 'total_amount', 'receipt', 'notes'],
  business_trip: ['destination', 'start_date', 'end_date', 'estimated_cost', 'purpose'],
  procurement_request: ['vendor_name', 'item_or_service', 'quantity', 'estimated_cost', 'business_need'],
  contract_approval: ['counterparty', 'contract_value', 'start_date', 'contract_file', 'summary'],
  onboarding: ['onboarding_note', 'new_joiner_name', 'department', 'buddy', 'start_date', 'position'],
  offboarding: ['departing_worker', 'last_working_day', 'reason', 'handover_notes'],
  local_acceptance_leave_request: ['worker_name', 'start_date', 'end_date', 'days_requested', 'leave_type', 'reason', 'organization_location', 'supporting_document'],
  local_acceptance_purchase_approval: ['description', 'estimated_cost', 'category', 'expense_date', 'delivery_location', 'invoice'],
  local_acceptance_it_access_request: ['system_or_resource', 'access_level', 'justification', 'organization_location'],
  local_acceptance_vendor_contract_review: ['vendor_name', 'contract_value', 'effective_date', 'expiry_date', 'contract_document', 'notes', 'organization_location'],
};

/** Exact option values emitted by scripts/seed_multi_orgs.py::build_process_seed_contracts. */
export const LOCAL_ACCEPTANCE_DEFINITION_OPTIONS: Partial<
  Record<LocalAcceptanceSystemKey, Record<string, readonly string[]>>
> = {
  local_acceptance_leave_request: {
    leave_type: ['Annual', 'Sick', 'Unpaid', 'Maternity'],
  },
  local_acceptance_purchase_approval: {
    category: ['Travel', 'Meals', 'Office Supplies', 'Software', 'Other'],
  },
  local_acceptance_it_access_request: {
    access_level: ['Read', 'Write', 'Administrator'],
  },
};

const SYSTEM_DEFINITION_NODES: Record<SystemDefinitionKey, readonly string[]> = {
  general_approval: ['start', 'manager_approval', 'approved_notification', 'rejected_notification', 'end'],
  purchase_request: ['start', 'amount_route', 'finance_approval', 'manager_approval', 'approved_notification', 'rejected_notification', 'end'],
  it_access_request: ['start', 'owner_approval', 'approved_notification', 'rejected_notification', 'end'],
  leave_request: ['start', 'length_route', 'skip_level_approval', 'manager_approval', 'approved_notification', 'rejected_notification', 'end'],
  overtime_request: ['start', 'manager_approval', 'approved_notification', 'rejected_notification', 'end'],
  expense_report: ['start', 'amount_route', 'finance_approval', 'manager_approval', 'approved_notification', 'rejected_notification', 'end'],
  business_trip: ['start', 'manager_approval', 'cost_route', 'finance_approval', 'approved_notification', 'rejected_notification', 'end'],
  procurement_request: ['start', 'manager_approval', 'owner_approval', 'approved_notification', 'rejected_notification', 'end'],
  contract_approval: ['start', 'owner_approval', 'finance_approval', 'approved_notification', 'rejected_notification', 'end'],
  onboarding: ['start', 'manager_approval', 'owner_approval', 'approved_notification', 'rejected_notification', 'end'],
  offboarding: ['start', 'manager_approval', 'finance_approval', 'approved_notification', 'rejected_notification', 'end'],
  local_acceptance_leave_request: ['start', 'approval', 'approved_notification', 'rejected_notification', 'end'],
  local_acceptance_purchase_approval: ['start', 'amount_route', 'finance_approval', 'manager_approval', 'approved_notification', 'rejected_notification', 'end'],
  local_acceptance_it_access_request: ['start', 'approval', 'approved_notification', 'rejected_notification', 'end'],
  local_acceptance_vendor_contract_review: ['start', 'owner_approval', 'finance_approval', 'archive_cc', 'approved_notification', 'rejected_notification', 'end'],
};

const SYSTEM_POLICY_DEFAULTS = [
  ['flow_rule', 'high_value_capex_routing', 'highValueCapexRouting'],
  ['flow_rule', 'standard_leave_routing', 'standardLeaveRouting'],
  ['auto_action', 'notify_requester_on_approval', 'notifyRequesterOnApproval'],
  ['auto_action', 'daily_overdue_task_digest', 'dailyOverdueTaskDigest'],
  ['exception_handler', 'sla_breach_escalation', 'slaBreachEscalation'],
] as const;

const SEED_MARKER_KEYS: Record<string, string> = {
  '__svdy_seed__:delegation_leave_cover': 'delegationLeaveCover',
  '__svdy_seed__:delegation_purchase_limit': 'delegationPurchaseLimit',
  '__svdy_seed__:delegation_historical_cover': 'delegationHistoricalCover',
  '__svdy_seed__:override_vendor_contract': 'overrideVendorContract',
  '__svdy_seed__:override_purchase_deadline': 'overridePurchaseDeadline',
  '__svdy_seed__:override_it_routing': 'overrideItRouting',
};

const FORM_PROPERTY_LABEL_KEYS: Record<string, string> = {
  placeholder: 'placeholder', maxLength: 'maxLength', required: 'required', rows: 'rows',
  min: 'minimum', max: 'maximum', step: 'step', allowPast: 'allowPastDates',
  allowFuture: 'allowFutureDates', options: 'optionsOnePerLine', allowClear: 'allowClear',
  // `maxSize`, not `maxSizeMb`. This map is keyed by the PROPERTY name, and nothing has ever
  // written `maxSizeMb` — the Designer wrote `maxSize`, so this entry labelled a key that did not
  // exist while the real one fell through to its raw English name. The i18n key keeps its
  // `maxSizeMb` name because "Max size (MB)" is the correct copy for it.
  maxSize: 'maxSizeMb', accept: 'accept', multiple: 'allowMultiple', currency: 'currency',
  departmentFilter: 'departmentFilter', inkColor: 'inkColor',
};

const FORM_DEFAULT_VALUE_KEYS: Record<string, string> = {
  'Enter text': 'enterText',
  'Option 1\nOption 2\nOption 3': 'option123',
  'A\nB\nC': 'abc',
};

function normalizeCode(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase().replace(/[\s/.-]+/g, '_');
}

function translateKnownCode(
  t: ProcessTranslator,
  value: string | null | undefined,
  keys: Record<string, string>,
  prefix: string,
): string {
  const original = value ?? '';
  const key = keys[normalizeCode(value)];
  return key ? t(`${prefix}.${key}`) : original;
}

export function translateProcessStatus(t: ProcessTranslator, value: string | null | undefined): string {
  return translateKnownCode(t, value, PROCESS_STATUS_KEYS, 'processes:processDesigner.statusOptions');
}
export function translateRoleCategory(t: ProcessTranslator, value: string | null | undefined): string {
  return translateKnownCode(t, value, ROLE_CATEGORY_KEYS, 'processes:roles');
}
export function translateLiveStatus(t: ProcessTranslator, value: string | null | undefined): string {
  return translateKnownCode(t, value, LIVE_STATUS_KEYS, 'processes:liveDisplay.status');
}
export function translatePriority(t: ProcessTranslator, value: string | null | undefined): string {
  return translateKnownCode(t, value, PRIORITY_KEYS, 'processes:liveDisplay.priority');
}
export function translateOutcome(t: ProcessTranslator, value: string | null | undefined): string {
  return translateKnownCode(t, value, OUTCOME_KEYS, 'processes:liveDisplay.outcome');
}
export function translateSlaStatus(t: ProcessTranslator, value: string | null | undefined): string {
  return translateKnownCode(t, value, SLA_KEYS, 'processes:liveDisplay.sla');
}
export function translateOverrideType(t: ProcessTranslator, value: string | null | undefined): string {
  return translateKnownCode(t, value, OVERRIDE_TYPE_KEYS, 'processes:liveDisplay.overrideType');
}
export function translateFieldFamily(t: ProcessTranslator, value: string | null | undefined): string {
  return translateKnownCode(t, value, FIELD_FAMILY_KEYS, 'processes:liveDisplay.fieldFamilies');
}
export function translateTemplateDomain(t: ProcessTranslator, value: string | null | undefined): string {
  return translateKnownCode(t, value, TEMPLATE_DOMAIN_KEYS, 'processes:liveDisplay.templateDomains');
}
export function translateFormDifficulty(t: ProcessTranslator, value: string | null | undefined): string {
  return translateKnownCode(t, value, FORM_DIFFICULTY_KEYS, 'processes:liveDisplay.formDifficulties');
}
export function translateFormPropertyType(t: ProcessTranslator, value: string | null | undefined): string {
  return translateKnownCode(t, value, FORM_PROPERTY_TYPE_KEYS, 'processes:liveDisplay.formPropertyTypes');
}
export function translateDelegationProcessType(t: ProcessTranslator, value: string | null | undefined): string {
  const original = value ?? '';
  const option = DELEGATION_PROCESS_OPTIONS.find((item) => item.value === original);
  return option ? t(`processes:liveDisplay.delegationProcessTypes.${option.i18nKey}`) : original;
}
export function translatePolicyOption(t: ProcessTranslator, value: string | null | undefined): string {
  const original = value ?? '';
  const option = POLICY_OPTIONS[normalizeCode(value)];
  return option ? t(option.i18nKey) : original;
}
export function translateTimelineAction(t: ProcessTranslator, value: string | null | undefined): string {
  return translateKnownCode(t, value, TIMELINE_ACTION_KEYS, 'processes:instanceDetail.timelineActions');
}

/**
 * Does this flow node have anything to SAY in a detail panel?
 *
 * Owner, 2026-09-09, on a completed instance parked on `End`: *"这个end是啥意思"*, then the rule —
 * *"当前节点，如果是End就可以不显示，除非当前节点非start和end才显示信息"*.
 *
 * `start` and `end` are structural: they carry no approver, no condition, no SLA and no branch, so the
 * panel could only ever repeat the node's own label and state chip and add a row reading `Type: End`.
 * That is not information, and on a finished request it reads as though something is still actionable.
 *
 * An UNKNOWN type answers TRUE. Failing toward showing is the safe direction here: a custom or
 * future node type may carry configuration, and hiding it would silently swallow real detail —
 * whereas showing a mostly-empty panel for one exotic node is merely plain (session-discipline §12).
 *
 * The engine dispatches `approval`, `condition`, `notification`, `cc` and `end` (`_execute_node`), so
 * in practice "has details" means the first four.
 *
 * Mobile twin: `svdy-mobile/src/utils/flow.ts::flowNodeHasDetails` — update in lockstep (svdy-ui §13).
 */
export function flowNodeHasDetails(nodeType: string | null | undefined): boolean {
  const code = normalizeCode(nodeType);
  return code !== 'start' && code !== 'end';
}

/** What a timeline entry MEANS, for anything that colours one. */
export type ProcessTimelineTone = 'success' | 'error' | 'warning' | 'accent' | 'neutral';

/**
 * The tone of a timeline entry, derived from WHAT HAPPENED.
 *
 * THE DEFECT this replaces (owner, production, 2026-09-09): the worker portal coloured the dot
 * `idx === 0 ? 'primary.main' : 'rgba(0,0,0,0.2)'`, so two identical `Approved` events read as two
 * different kinds of event and the accent landed on whichever row happened to be first.
 *
 * And the index was not merely arbitrary, it was INVERTED against its own intent: the endpoint orders
 * `InstanceTimeline` by `created_at` ASCENDING, so `idx === 0` is the OLDEST entry while a lone accent
 * dot plainly means "the latest thing that happened". It only ever looked plausible because the oldest
 * entry is `started`, which is the one event that does deserve the accent.
 *
 * Returns a TONE, not a colour: the palette belongs to the surface (MUI tokens on web, `colors.*` on
 * the phone), while the meaning belongs here. Every action in `TIMELINE_ACTION_KEYS` is accounted for,
 * and that list is itself measured from `_add_timeline(...)` in the engine by `processCodeTables.test.ts`
 * — so a new server-side action cannot silently fall into a default.
 */
export function processTimelineTone(
  action: string | null | undefined,
  outcome?: string | null,
): ProcessTimelineTone {
  const code = normalizeCode(action);
  const result = normalizeCode(outcome);
  // A decision, whether it is recorded on the task event or as the outcome of the node that carried it.
  if (code === 'task_approved' || result === 'approved') return 'success';
  if (code === 'task_rejected' || result === 'rejected') return 'error';
  // Ended without a decision. Not an error — nothing failed — but the reader must not read it as a
  // routine step either, which is what a neutral dot would say.
  if (code === 'cancelled' || code === 'withdrawn' || result === 'cancelled') return 'warning';
  // The request beginning is the one structural event worth an accent.
  if (code === 'started') return 'accent';
  // `node_entered`, `branched`, `task_spawned`, `rerouted`, `retried`, `task_forwarded`,
  // `task_reassigned`: the machinery moving. Real, and not something to draw the eye.
  return 'neutral';
}
export function translateFormComponentCode(t: ProcessTranslator, value: string | null | undefined): string {
  const original = value ?? '';
  const canonical = SYSTEM_FORM_COMPONENT_DEFAULTS.find(([, name]) => name === original);
  return canonical
    ? t(`processes:liveDisplay.systemFormComponents.${canonical[0]}.label`)
    : original;
}
export function formatProcessDate(value: string | null | undefined, locale: string): string {
  if (!value) return '';
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(locale);
}

export function getProcessRoleDisplay(t: ProcessTranslator, role: RoleDisplayInput): DisplayText {
  const description = role.description ?? '';
  if (role.category !== 'system') return { name: role.name, description };
  const canonical = SYSTEM_ROLE_DEFAULTS.find(([, name]) => role.name === name);
  if (!canonical) return { name: role.name, description };
  const [key] = canonical;
  return {
    name: t(`processes:roles.systemDefaults.${key}.name`),
    description: t(`processes:roles.systemDefaults.${key}.description`),
  };
}

export function getProcessNodeDisplay(t: ProcessTranslator, node: NodeDisplayInput): DisplayText {
  const description = node.description ?? '';
  if (node.configuration?.svdy_system_default !== true) return { name: node.name, description };
  const canonical = SYSTEM_NODE_DEFAULTS.find(([, name, type]) =>
    node.name === name && node.type === type,
  );
  if (!canonical) return { name: node.name, description };
  const [key] = canonical;
  return {
    name: t(`processes:nodes.systemDefaults.${key}.name`),
    description: t(`processes:nodes.systemDefaults.${key}.description`),
  };
}

export function getFormComponentDisplay(
  t: ProcessTranslator,
  component: FormComponentDisplayInput,
): FormComponentDisplay {
  const description = component.description ?? '';
  const properties = component.properties ?? [];
  if (!component.isSystem) {
    return { name: component.name, label: component.label, description, properties };
  }
  const canonical = SYSTEM_FORM_COMPONENT_DEFAULTS.find(([, slug]) => slug === component.name);
  if (!canonical) return { name: component.name, label: component.label, description, properties };
  const [key] = canonical;
  return {
    name: component.name,
    label: t(`processes:liveDisplay.systemFormComponents.${key}.label`),
    description: t(`processes:liveDisplay.systemFormComponents.${key}.description`),
    properties: properties.map((property) => {
      const labelKey = FORM_PROPERTY_LABEL_KEYS[property.name];
      const defaultKey = typeof property.defaultValue === 'string'
        ? FORM_DEFAULT_VALUE_KEYS[property.defaultValue]
        : undefined;
      return {
        ...property,
        label: labelKey
          ? t(`processes:liveDisplay.formProperties.${labelKey}`)
          : property.label,
        defaultValue: defaultKey
          ? t(`processes:liveDisplay.formDefaultValues.${defaultKey}`)
          : property.defaultValue,
      };
    }),
  };
}

export function getFormTemplateDisplay(t: ProcessTranslator, template: FormTemplateDisplayInput): DisplayText {
  const description = template.description ?? '';
  if (!template.isSystem) return { name: template.name, description };
  const canonical = SYSTEM_FORM_TEMPLATE_DEFAULTS.find(([, name]) => template.name === name);
  if (!canonical) return { name: template.name, description };
  const [key] = canonical;
  return {
    name: t(`processes:liveDisplay.systemFormTemplates.${key}.name`),
    description: t(`processes:liveDisplay.systemFormTemplates.${key}.description`),
  };
}

/**
 * The built-in a definition's text MAY be SVDY's: a template's own `system_key`, or the
 * server-managed `form_schema.origin_system_key` of a copy made from it. On its own this key
 * translates nothing. Every function below also requires the string to still be that built-in's
 * English for the same element (`isSystemText`). An edited or added string is the tenant's and
 * stays as stored. Backend twin: app/processes/provenance.py.
 */
type KeySource = Pick<ProcessDefinitionDisplayInput, 'system_key' | 'systemKey' | 'form_schema' | 'formSchema'>;

function knownKey(candidate: string | null | undefined): SystemDefinitionKey | undefined {
  return candidate && Object.prototype.hasOwnProperty.call(SYSTEM_DEFINITION_KEYS, candidate)
    ? candidate as SystemDefinitionKey
    : undefined;
}

/**
 * A TEMPLATE's own key, top-level only (the protected column): provisioner-written and never
 * tenant-edited, so all of its text is SVDY's. A key inside `form_schema` is not trusted on its own.
 */
function trustedKey(source: KeySource): SystemDefinitionKey | undefined {
  return knownKey(source.system_key ?? source.systemKey);
}

export function systemDefinitionKey(source: KeySource): SystemDefinitionKey | undefined {
  const schema = source.form_schema ?? source.formSchema;
  return trustedKey(source) ?? knownKey(schema?.origin_system_key ?? schema?.system_key);
}

/**
 * May this string of `definition` be shown in the reader's language? A template's text: yes. A
 * copy's text: only while it is still the built-in's English for that element (the tenant has not
 * edited it). Anything else: no.
 */
function translatable(
  definition: KeySource,
  kind: SystemTextKind,
  raw: string | null | undefined,
  elementId?: string | null,
  optionCode?: string,
): boolean {
  if (trustedKey(definition)) return true;
  return isSystemText(systemDefinitionKey(definition), kind, raw, elementId ?? undefined, optionCode);
}

type SystemTextKind = 'names' | 'descriptions' | 'fields' | 'texts' | 'nodes' | 'options';

/** Is `raw` still the built-in's own English for this element? (lower-cased, trimmed). */
export function isSystemText(
  key: SystemDefinitionKey | undefined,
  kind: SystemTextKind,
  raw: string | null | undefined,
  elementId?: string | null,
  optionCode?: string,
): boolean {
  const entry = key ? PROCESS_SYSTEM_SOURCE[key] : undefined;
  const text = (raw ?? '').trim().toLowerCase();
  if (!entry || !text) return false;
  if (kind === 'names' || kind === 'descriptions') return entry[kind].includes(text);
  if (!elementId) return false;
  if (kind === 'options') return (entry.options[elementId]?.[optionCode ?? ''] ?? []).includes(text);
  return (entry[kind][elementId] ?? []).includes(text);
}

export function getProcessDefinitionDisplay(
  t: ProcessTranslator,
  definition: ProcessDefinitionDisplayInput,
): DisplayText {
  const key = systemDefinitionKey(definition);
  const name = definition.name;
  const description = definition.description ?? '';
  if (!key) return { name, description };
  return {
    name: translatable(definition, 'names', name) ? t(`processes:liveDisplay.systemDefinitions.${key}.name`) : name,
    description: translatable(definition, 'descriptions', description)
      ? t(`processes:liveDisplay.systemDefinitions.${key}.description`)
      : description,
  };
}

export function getProcessDefinitionFieldLabel(
  t: ProcessTranslator,
  definition: ProcessDefinitionDisplayInput,
  field: DefinitionFieldDisplayInput,
  index = 0,
): string {
  const fallback = field.properties?.label ?? field.label ?? field.id ?? `field_${index}`;
  const key = systemDefinitionKey(definition);
  if (!key || !field.id || !SYSTEM_DEFINITION_FIELDS[key].includes(field.id)) return fallback;
  if (!translatable(definition, 'fields', fallback, field.id)) return fallback;
  return t(`processes:liveDisplay.systemDefinitions.${key}.fields.${field.id}`);
}

export function getProcessDefinitionFieldOptions(
  t: ProcessTranslator,
  definition: ProcessDefinitionDisplayInput,
  field: DefinitionFieldDisplayInput,
): Array<{ label: string; value: string }> {
  const options = field.properties?.options ?? [];
  const normalized = options.map((option) => typeof option === 'string'
    ? { label: option, value: option }
    : option);
  const key = systemDefinitionKey(definition);
  if (!key || !field.id || !SYSTEM_DEFINITION_FIELDS[key].includes(field.id)) return normalized;
  return normalized.map((option) => {
    const optionKey = normalizeCode(option.value);
    if (!translatable(definition, 'options', option.label, field.id, optionKey)) return option;
    const translated = t(`processes:liveDisplay.systemDefinitions.${key}.options.${field.id}.${optionKey}`, {
      defaultValue: option.label,
    });
    return { ...option, label: translated };
  });
}

export function getProcessDefinitionNodeLabel(
  t: ProcessTranslator,
  definition: ProcessDefinitionDisplayInput,
  node: DefinitionNodeDisplayInput,
): string {
  const fallback = node.data?.label ?? node.label ?? '';
  const key = systemDefinitionKey(definition);
  if (!key || !node.id || !SYSTEM_DEFINITION_NODES[key].includes(node.id)) return fallback;
  if (!translatable(definition, 'nodes', fallback, node.id)) return fallback;
  return t(`processes:liveDisplay.systemDefinitions.${key}.nodes.${node.id}`);
}

export function getSystemPolicyDisplay(
  t: ProcessTranslator,
  policy: PolicyDisplayInput,
): DisplayText {
  const description = policy.description ?? '';
  if (!policy.isSystem) return { name: policy.name, description };
  const canonical = SYSTEM_POLICY_DEFAULTS.find(([kind, slug]) =>
    kind === policy.kind && slug === normalizeCode(policy.name));
  if (!canonical) return { name: policy.name, description };
  const key = canonical[2];
  return {
    name: t(`processes:liveDisplay.systemPolicies.${key}.name`),
    description: t(`processes:liveDisplay.systemPolicies.${key}.description`),
  };
}

export function getDelegationConditionsDisplay(t: ProcessTranslator, value: string | null | undefined): string {
  const text = value ?? '';
  const markerKey = SEED_MARKER_KEYS[text];
  return markerKey ? t(`processes:liveDisplay.seedMarkers.${markerKey}`) : text;
}

export function getOverrideReasonDisplay(t: ProcessTranslator, value: string | null | undefined): string {
  return getDelegationConditionsDisplay(t, value);
}

export function getProcessDefinitionFieldText(
  t: ProcessTranslator,
  definition: ProcessDefinitionDisplayInput,
  field: DefinitionFieldDisplayInput,
): string {
  const fallback = typeof field.properties?.text === 'string' ? field.properties.text : '';
  const key = systemDefinitionKey(definition);
  if (!key || !field.id || !SYSTEM_DEFINITION_FIELDS[key].includes(field.id)) return fallback;
  if (!translatable(definition, 'texts', fallback, field.id)) return fallback;
  return t(`processes:liveDisplay.systemDefinitions.${key}.staticText.${field.id}`, {
    defaultValue: fallback,
  });
}


/**
 * What ONE timeline row says: which step, and a note only when it adds something.
 *
 * TWO SURFACES HAD TWO RENDERERS AND BOTH WERE WRONG (owner, production, 2026-09-09, two screenshots):
 *
 *   worker portal   `Approval task created — Manager approval — Manager approval`
 *   admin           `Reached this step`            (no step name at all)
 *
 * The duplication is arithmetic, not a rendering fault. `engine._handle_approval` writes
 * `note=task.title`, and a task's title IS the node's label — so once `node_id` started resolving to that
 * same label on 2026-09-09, the row printed it twice. The admin page never resolved `node_id`, so it
 * printed neither: `Reached this step` three times with identical timestamps, which is what the owner
 * means by the two surfaces being inconsistent.
 *
 * One function, both surfaces, so a row cannot say different things depending on who is looking — the
 * same move `SubmittedApplication` made for five surfaces that had four formatters.
 *
 * `note` is dropped when it duplicates the step, compared case- and whitespace-insensitively: a note is
 * also free text an approver can write ("Confirmed."), and THAT is worth showing.
 */
export function processTimelineParts(
  stepLabel: string | null | undefined,
  notes: string | null | undefined,
): { step?: string; note?: string } {
  const step = (stepLabel ?? '').trim() || undefined;
  const note = (notes ?? '').trim() || undefined;
  if (step && note && step.toLowerCase() === note.toLowerCase()) {
    return { step };
  }
  return { step, note };
}

/**
 * The step label for a timeline event, from the instance's own flow SNAPSHOT.
 *
 * The snapshot rather than the definition: a running request must be described by the flow it started
 * under. `resolve` is the seeded-built-in localizer, passed in for the same reason `FlowViewer` takes one —
 * this module must stay free of the Processes page tree.
 */
export function timelineStepLabel(
  flowSnapshot: unknown,
  nodeId: string | null | undefined,
  resolve?: (node: { id?: string; data?: { label?: string } }) => string | undefined,
): string | undefined {
  if (!nodeId) return undefined;
  const nodes = (flowSnapshot as { nodes?: { id?: string; data?: { label?: string } }[] } | null)?.nodes;
  const node = Array.isArray(nodes) ? nodes.find((candidate) => candidate?.id === nodeId) : undefined;
  if (!node) return undefined;
  return resolve?.(node) || node.data?.label || undefined;
}

/**
 * The timeline rows a READER should see. Everything else stays in the API as audit, and is not rendered.
 *
 * Owner, 2026-09-09, on a five-step flow rendering ten rows: *"timeline的噪音对读者没意义可以不显示"*.
 * Measured on the screenshot — `Request started` and `Step entered — Submit` carried the SAME timestamp,
 * the SAME actor and the same fact; `Approval task created — Manager approval` carried the same timestamp
 * as the `Step entered` immediately above it; `Step entered — End` said nothing the terminal status does
 * not. Ten rows, four of them restating their neighbour.
 *
 * FILTERED AT THE READ, NOT AT THE SOURCE. These events are legitimate audit records — `task_spawned` is
 * when the obligation was created, and an approval trail needs it. Dropping them from the response would
 * take them from every consumer including the phone. The reader's story and the audit record are two
 * different products of one table, which is why this is a display decision.
 *
 * A WHITELIST BY EXCEPTION: three named exclusions, each with the reason it duplicates a neighbour.
 * Everything the engine writes is otherwise shown, so a NEW action appears by default rather than being
 * silently swallowed — the failure mode of a positive whitelist nobody remembers to extend.
 */
export function visibleTimelineEvents<T extends {
  action?: string | null; node_id?: string | null; created_at?: string | null;
}>(events: readonly T[] | null | undefined, flowSnapshot: unknown): T[] {
  const nodes = (flowSnapshot as { nodes?: Array<{ id?: string; type?: string }> } | null)?.nodes;
  const nodeType = (nodeId: string | null | undefined): string => {
    if (!nodeId || !Array.isArray(nodes)) return '';
    return (nodes.find((node) => node?.id === nodeId)?.type ?? '').toLowerCase();
  };

  return (events ?? []).filter((event) => {
    const action = (event.action ?? '').toLowerCase();

    // 1. `task_spawned` — the obligation was created at the same instant the step was entered, on the
    //    same node, and `_handle_approval` names it with the node's own label. It restates the row above
    //    it word for word.
    if (action === 'task_spawned') return false;

    if (action === 'node_entered') {
      const type = nodeType(event.node_id);
      // 2. The START node — `started` already said this, with the same timestamp and the same person.
      if (type === 'start') return false;
      // 3. The END node — the request's terminal status says it, and "reached End" reads as if something
      //    were still to happen.
      if (type === 'end') return false;
    }

    return true;
  });
}

// ---------------------------------------------------------------------------
// PROVENANCE: built-in process text follows the reader, tenant text stays as designed (2026-10-30).
//
// A definition with `system_key` is SVDY's own, unedited copy (only the provisioner writes the key, keyed
// definitions are active and not editable, and every copy strips it). Its name and steps are shown in the
// reader's language. Anything else was designed by the tenant — in English, Chinese, Arabic — and is shown
// exactly as designed. Never decide by what the text SAYS: a tenant step called "Manager approval" is the
// tenant's words. Mobile twin: svdy-mobile/src/i18n/processSystem.ts — update in lockstep.
// ---------------------------------------------------------------------------

type ProvenanceCarrier = {
  system_key?: string | null;
  systemKey?: string | null;
  definition_system_key?: string | null;
  form_schema?: unknown;
  request_form_schema?: unknown;
} | null | undefined;

function nestedKey(schema: unknown): string | undefined {
  return schema && typeof schema === 'object'
    ? (((schema as { system_key?: unknown }).system_key
      ?? (schema as { origin_system_key?: unknown }).origin_system_key) as string | undefined) ?? undefined
    : undefined;
}

/** The built-in key a request / task / definition carries, or undefined for tenant-designed content. */
export function processProvenanceKey(carrier: ProvenanceCarrier): SystemDefinitionKey | undefined {
  if (!carrier) return undefined;
  const candidate = carrier.system_key ?? carrier.systemKey ?? carrier.definition_system_key
    ?? nestedKey(carrier.form_schema) ?? nestedKey(carrier.request_form_schema);
  // `nestedKey` reads a copy's `origin_system_key` too; what that allows is still gated per string.
  return systemDefinitionKey({ system_key: candidate ?? null });
}

/**
 * A REQUEST title — the server snapshots the definition name at submission. Localized only for a built-in
 * AND only while it is still that built-in's English name; any other title is shown as stored.
 */
export function getSystemRequestTitle(t: ProcessTranslator, carrier: ProvenanceCarrier, title: string | null | undefined): string {
  const raw = title ?? '';
  const key = processProvenanceKey(carrier);
  // A snapshot: compared to the built-in's English whatever the carrier (the tenant may have renamed it).
  if (!key || !isSystemText(key, 'names', raw)) return raw;
  return getProcessDefinitionDisplay(t, { system_key: key, name: raw }).name;
}

/** A TASK title — the engine snapshots the step label. Localized by NODE ID for a built-in only. */
export function getSystemTaskTitle(
  t: ProcessTranslator,
  carrier: ProvenanceCarrier,
  nodeId: string | null | undefined,
  title: string | null | undefined,
): string {
  const raw = title ?? '';
  const key = processProvenanceKey(carrier);
  if (!key || !nodeId || !isSystemText(key, 'nodes', raw, nodeId)) return raw;
  return getProcessDefinitionNodeLabel(t, { system_key: key, name: '' }, { id: nodeId, label: raw });
}

/**
 * Interpolation args of a process notification, with `{{title}}` localized ONLY when the server attached
 * a built-in `system_key` (+ `node_id` for a step) — `app/processes/notify_args.py`. Same rule as the push
 * renderer (`app/shared/i18n.localize_process_args`), so the bell and the lock screen agree.
 */
export function localizeProcessNotificationArgs(
  t: ProcessTranslator,
  args: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const out = { ...(args ?? {}) };
  const key = systemDefinitionKey({ system_key: typeof out.system_key === 'string' ? out.system_key : null });
  if (!key || typeof out.title !== 'string') return out;
  const nodeId = typeof out.node_id === 'string' ? out.node_id : undefined;
  if (!isSystemText(key, nodeId ? 'nodes' : 'names', out.title, nodeId)) return out;
  out.title = nodeId
    ? getProcessDefinitionNodeLabel(t, { system_key: key, name: '' }, { id: nodeId, label: out.title })
    : getProcessDefinitionDisplay(t, { system_key: key, name: out.title }).name;
  return out;
}
